<template>
  <div class="cw-world-toggle font-family-glegoo">
    <RouterLink
      v-for="one in worlds"
      :key="one.title"
      v-ga-event="{
        interaction: 'button_click',
        sectionContent: one.title,
        sectionNumber: 10,
        sectionName: 'Game Tap',
      }"
      class="cw-world-toggle__link"
      :class="[`cw-world-toggle__link--${one.key}`, { 'cw-world-toggle__link--active': one.active }]"
      :to="one.route"
      v-text="one.title"
      @click="saveSelectedGameVersion(one.version)"
    />
  </div>
</template>
<script setup lang="ts">
  import { selectedGameVersion, type GameVersion } from '@/helpers/selectedGameVersion'

  // Labels are fixed by design; only the destinations vary per page, so a World Map pair
  // passes its own two routes. Normally rendered from `meta.worldToggle` by MainMenu
  // rather than placed by hand — see `worldToggle` in src/router/router.ts.
  const props = defineProps({
    modernRoute: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      required: true,
    },
    classicRoute: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      required: true,
    },
  })

  const route = useRoute()
  const router = useRouter()

  // Remember the last pick so the navigation menu opens that world next time (see MenuLink).
  // Saved on click rather than on route change: landing on a Classic page via a direct link
  // is not a choice.
  function saveSelectedGameVersion(version: GameVersion) {
    selectedGameVersion.value = version
  }

  // Match on the resolved route NAME, not on the path. Names are what every link here uses
  // and they survive a move: the Classic pages sit under /game/mscw/ and carry the modern
  // page's own slug (`mscw/explore-maplestory`), so any path test would need rewriting again.
  function isActive(target: RouteLocationRaw) {
    const resolved = router.resolve(target)
    return route.matched.some((one) => !!one.name && one.name === resolved.name)
  }

  const worlds = computed(() => {
    const list = [
      { key: 'modern', version: 'ms' as const, title: 'MapleStory', route: props.modernRoute },
      { key: 'classic', version: 'mscw' as const, title: 'MapleStory Classic World', route: props.classicRoute },
    ].map((one) => ({ ...one, active: isActive(one.route) }))
    // never leave the control blank — fall back to the modern side when neither matches
    if (!list.some((one) => one.active)) list[0].active = true
    return list
  })
</script>
<style lang="scss" scoped>
  .cw-world-toggle {
    // sits inside MainMenu's .page-header, which holds an absolutely positioned
    // .header-image — so it needs its own stacking context to stay clickable
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    column-gap: 5px;
    width: 515px;
    margin-top: 16px;
    padding: 5px;
    border-radius: 25px;
    background-color: rgba(55, 40, 30, 0.9);
    backdrop-filter: blur(10px);
  }
  .cw-world-toggle__link {
    // 40px inner pill + the 5px wrapper padding = the design's 515x50
    flex: 0 1 0;
    padding: 8px 0;
    border-radius: 20px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    text-transform: uppercase;
    text-decoration: none;
    color: white;
    transition: background-color 0.3s;
  }
  // The two halves are NOT equal: Figma gives the short label a 170 pill and the long one
  // 330, inside the 500 that the 5px padding and 5px gap leave of the 515 track. Weighted
  // flex-grow holds that 170:330 split at any width instead of pinning it to 515. These
  // must stay after the base rule — same specificity, so source order decides.
  .cw-world-toggle__link--modern {
    flex-grow: 170;
  }
  .cw-world-toggle__link--classic {
    flex-grow: 330;
  }
  .cw-world-toggle__link--active {
    background-color: var(--theme-blue-active-color);
  }
  .cw-world-toggle__link:not(.cw-world-toggle__link--active):hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  // Below the 1600 content column the header has less room (see MainMenu's taller ratio for
  // `has-world-toggle`), so the control sits closer to the subtitle. The label stays at the
  // desktop 20px - only tablet and below step it down.
  @media #{$screen} and (min-width: #{$tabletSize + 1}) and (max-width: #{$contentMaxWidthSize}) {
    .cw-world-toggle {
      margin-top: 8px;
    }
  }

  // The 1024 artboard draws the toggle at the same 515x50 as the 1920 one, 20px labels
  // included — it does not scale down — so tablet keeps the base control and changes only the
  // top margin. (A 16px label here made the pill 45 tall, 5 short of the artboard.)
  @media #{$tablet} {
    .cw-world-toggle {
      margin-top: 12px;
    }
  }
  // The 600 artboard also draws it 515x50, centred — so the width is the artboard's and
  // only the cap below it is ours: 515 cannot fit a 390 viewport. Once the control is
  // capped, a fixed 20px label no longer fits the 330 pill, so the label is measured
  // against the control itself. It reads exactly 20px at 515 and shrinks only when the
  // control has actually been squeezed.
  @media #{$mobile} {
    .cw-world-toggle {
      align-self: center;
      width: 515px;
      max-width: 100%;
      margin-top: 10px;
      container-type: inline-size;
    }
    .cw-world-toggle__link {
      font-size: min(20px, math.div(20, 515) * 100cqw);
    }
  }
</style>
